import type { IVariantIntervalSeries } from './useFetchCampaignAnalyticDetailV2';
import type { AnalyticInterval, EAnalyticMode } from '~/modules/analytics/types';
import type { ICampaignGroup } from '~/modules/campaign-group';
import type { AnalyticDateFilter } from '~/presentation/ui';
import type { IAnalyticMetric } from '~/types/metric';

import { extractCampaignGroupDetailData } from '../helpers/extractCampaignGroupDetailData';
import { useCustomCampaignAnalyticQueryV2 } from '../api/useCustomCampaignAnalyticQueryV2';
import { buildCampaignGroupQueryVariables } from '../helpers/campaignGroupAnalyticV2';
import { TIME_DIMENSION_TO_INTERVAL } from '../helpers/timeDimensionToInterval';
import { getTimeDimensionByDate } from '~/core/gemxql/helpers';
import { captureSentryError } from '~/exceptions/sentry';
import { IVariantType } from '~/modules/campaign/types';
import { useEffect, useMemo } from 'react';

export interface IFetchCampaignGroupAnalyticDetailV2Props {
  /** Pass `undefined` to disable both fetches (e.g. listing-card path or draft groups). */
  campaignGroup?: ICampaignGroup;
  currentDates: AnalyticDateFilter;
  previousDates: AnalyticDateFilter;
  isQueryPreviousData?: boolean;
  analyticMode: EAnalyticMode;
  /** Listing cards only need the totals, so they skip the TIMESERIES part of the query. */
  isFetchMetricInterval?: boolean;
}

export interface IFetchCampaignGroupAnalyticDetailV2Result {
  metricsA?: IAnalyticMetric;
  metricsB?: IAnalyticMetric;
  metricsPreviousA?: IAnalyticMetric;
  metricsPreviousB?: IAnalyticMetric;
  metricInterval: IVariantIntervalSeries[];
  interval: AnalyticInterval;
  isLoadingMetric: boolean;
  isLoadingMetricInterval: boolean;
}

export const useFetchCampaignGroupAnalyticDetailV2 = ({
  campaignGroup,
  currentDates,
  previousDates,
  isQueryPreviousData = true,
  analyticMode,
  isFetchMetricInterval = true,
}: IFetchCampaignGroupAnalyticDetailV2Props): IFetchCampaignGroupAnalyticDetailV2Result => {
  const timeDimension = useMemo(
    () => getTimeDimensionByDate(currentDates.startDate, currentDates.endDate),
    [currentDates.startDate, currentDates.endDate],
  );
  const interval = isFetchMetricInterval ? timeDimension : undefined;

  const campaignGroupID = campaignGroup?.id ?? '';
  // Scoped only when the picked range is a version range; presets and the compare window are not.
  const campaignGroupVersionID = currentDates.versionId;

  const currentVariables = useMemo(
    () =>
      buildCampaignGroupQueryVariables({
        mode: analyticMode,
        campaignGroupID,
        campaignGroupVersionID,
        dateRange: currentDates,
        interval,
      }),
    [analyticMode, campaignGroupID, campaignGroupVersionID, currentDates, interval],
  );

  const previousVariables = useMemo(
    () =>
      buildCampaignGroupQueryVariables({
        mode: analyticMode,
        campaignGroupID,
        dateRange: previousDates,
        interval,
      }),
    [analyticMode, campaignGroupID, previousDates, interval],
  );

  const isEnabled = !!campaignGroupID && !!currentVariables.input;
  const isEnabledPrevious = isEnabled && isQueryPreviousData && !!previousVariables.input;

  const { data: currentResponse, isLoading: isLoadingCurrent } = useCustomCampaignAnalyticQueryV2(
    currentVariables,
    {
      enabled: isEnabled,
    },
  );

  const { data: previousResponse, isLoading: isLoadingPrevious } = useCustomCampaignAnalyticQueryV2(
    previousVariables,
    {
      enabled: isEnabledPrevious,
    },
  );

  const currentExtract = useMemo(
    () => extractCampaignGroupDetailData({ response: currentResponse, interval }),
    [currentResponse, interval],
  );

  const previousExtract = useMemo(
    () => extractCampaignGroupDetailData({ response: previousResponse, interval }),
    [previousResponse, interval],
  );

  // Dep keyed on content so the effect re-fires only when the unknown set actually changes;
  // otherwise every parent re-render allocates a new array and would spam Sentry.
  const unknownVersionKey = [
    ...new Set([...currentExtract.unknownVersions, ...previousExtract.unknownVersions]),
  ]
    .sort()
    .join(',');

  useEffect(() => {
    if (!unknownVersionKey) return;
    captureSentryError(new Error('extractCampaignGroupDetailData: version is neither A nor B'), {
      source: 'useFetchCampaignGroupAnalyticDetailV2',
      tags: { analytic: 'Analytic' },
      extra: { campaignGroupID, unknownVersions: unknownVersionKey.split(',') },
    });
  }, [campaignGroupID, unknownVersionKey]);

  const metricInterval = useMemo<IVariantIntervalSeries[]>(
    () => [
      {
        data: currentExtract.intervalByVariant[IVariantType.VARIANT_A] ?? [],
        variantKey: IVariantType.VARIANT_A,
      },
      {
        data: currentExtract.intervalByVariant[IVariantType.VARIANT_B] ?? [],
        variantKey: IVariantType.VARIANT_B,
      },
    ],
    [currentExtract.intervalByVariant],
  );

  const isLoadingMetric =
    (isEnabled && isLoadingCurrent) || (isEnabledPrevious && isLoadingPrevious);

  return {
    metricsA: currentExtract.totalsByVariant[IVariantType.VARIANT_A],
    metricsB: currentExtract.totalsByVariant[IVariantType.VARIANT_B],
    metricsPreviousA: previousExtract.totalsByVariant[IVariantType.VARIANT_A],
    metricsPreviousB: previousExtract.totalsByVariant[IVariantType.VARIANT_B],
    metricInterval,
    interval: TIME_DIMENSION_TO_INTERVAL[timeDimension],
    isLoadingMetric,
    isLoadingMetricInterval: isLoadingMetric,
  };
};
